import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { Layout, Menu, Tag } from 'antd';
import { withBreakpoints } from 'react-breakpoints';
import Logo from '../../components/Logo';
import IconSvg from '../../components/common/IconSvg';
import { buttonNormal, buttonHover } from '../../constants/themes';

const { Sider: AntSider } = Layout;
const MAIN_PAGE_KEY = 'dashboard';

const MenuItemContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;
const SiderTitleWrapper = styled.div`
  line-height: normal;
  text-align: center;
  margin-top: 10px;
  white-space: pre-wrap;
}
`;

const StyledMenuItem = styled(Menu.Item)`
  height: 75px !important;
  width: 100% !important;
  align-items: center;
  justify-content: center;
  align-self: center;
  justify-items: center;
  justify-self: center;
  align-content: center;
  vertical-align: middle;
  display: flex;
  flex-direction: column;
  margin-top: 0;
  margin-bottom: 0;
`;

const StyledMenu = styled(Menu)`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  height: calc(100vh - 68px);
  ${process.env.APP_NAME === 'SALEFREAKS' &&
  `.ant-menu-item-selected {
    background-color: #fcf1f0 !important;
  };
.ant-menu-inline, .ant-menu-item:after {
    border-right: 3px solid #ff3f71 !important;
  }
  `};
  ${({ fontSize }) =>
    fontSize
      ? `
  .ant-menu-item div {
    font-size: ${fontSize};
  }`
      : ''}
`;

@inject('onboardingStore')
@inject('routingStore')
@inject('localeStore')
@inject('notificationsStore')
@inject('settingsStore')
@observer
class Sider extends Component {
  static propTypes = {
    onboardingStore: PropTypes.object,
    routingStore: PropTypes.object,
    localeStore: PropTypes.object,
    notificationsStore: PropTypes.object,
  };

  componentDidMount() {
    const { routingStore } = this.props;
    routingStore.setRouteMainPath();
    routingStore.browserHistory.listen(() => {
      routingStore.setRouteMainPath();
    });
  }

  updateRouteState = path => {
    const {
      routingStore,
      notificationsStore: { fetchFirstPage },
    } = this.props;
    fetchFirstPage();
    if (window.location.pathname === path) {
      switch (path) {
        case '/discover/main': {
          routingStore.reloadDiscoverAndList();
          break;
        }
        case '/listings/table': {
          routingStore.reloadListings();
          break;
        }
        case '/orders/table': {
          routingStore.reloadOrders();
          break;
        }
        default: {
          break;
        }
      }
    }
    routingStore.browserHistory.push(path);
  };

  render() {
    const {
      routingStore: { routeMainPath },
      localeStore: { isBG, isFR },
      settingsStore: { subscription: { userHasCourse } }
    } = this.props;

    const { mobileLayout } = this.props;

    return (
      <AntSider
        css={{ backgroundColor: '#ffffff', overflow: 'hidden', position: !mobileLayout && 'fixed' }}
        width="118px"
      >
        {!mobileLayout && <Logo onClick={() => this.updateRouteState('/')} />}
        <StyledMenu
          mode="inline"
          defaultOpenKeys={[routeMainPath]}
          selectedKeys={[routeMainPath]}
          fontSize={isBG || isFR ? '10px' : null}
        >
          <StyledMenuItem key={MAIN_PAGE_KEY} onClick={() => this.updateRouteState('/')}>
            <MenuItemContainer>
              <IconSvg type="dashboard" color={buttonNormal} hoverColor={buttonHover} size={20} />
              <SiderTitleWrapper>
                <FormattedMessage id="common.dashboard" />
              </SiderTitleWrapper>
            </MenuItemContainer>
          </StyledMenuItem>
          <StyledMenuItem
            key="discover"
            onClick={() => {
              const { onboardingStore } = this.props;
              if (onboardingStore.flow === 'tutorial' && onboardingStore.tutorialStepIndex === 0) {
                onboardingStore.startTransition();
              }
              this.updateRouteState('/discover/main');
            }}
          >
            <MenuItemContainer>
              <IconSvg
                type="discover_and_list"
                color={buttonNormal}
                hoverColor={buttonHover}
                size={20}
              />
              <SiderTitleWrapper>
                <FormattedMessage id="common.discoverAndList" />
              </SiderTitleWrapper>
            </MenuItemContainer>
          </StyledMenuItem>
          <StyledMenuItem key="catalog" onClick={() => this.updateRouteState('/catalog')}>
            <MenuItemContainer>
              <IconSvg type="catalog" color={buttonNormal} hoverColor={buttonHover} size={20} />
              <FormattedMessage id="common.catalog" />
            </MenuItemContainer>
          </StyledMenuItem>
          <StyledMenuItem key="listings" onClick={() => this.updateRouteState('/listings/table')}>
            <MenuItemContainer>
              <IconSvg type="listings" color={buttonNormal} hoverColor={buttonHover} size={20} />
              <FormattedMessage id="common.listings" />
            </MenuItemContainer>
          </StyledMenuItem>
          <StyledMenuItem key="orders" onClick={() => this.updateRouteState('/orders/table')}>
            <MenuItemContainer>
              <IconSvg type="orders" color={buttonNormal} hoverColor={buttonHover} size={20} />
              <FormattedMessage id="common.orders" />
            </MenuItemContainer>
          </StyledMenuItem>
          <StyledMenuItem key="reports" onClick={() => this.updateRouteState('/reports')}>
            <MenuItemContainer>
              <IconSvg type="reports" color={buttonNormal} hoverColor={buttonHover} size={20} />
              <FormattedMessage id="common.reports" />
            </MenuItemContainer>
          </StyledMenuItem>
          <StyledMenuItem
            key="source-accounts"
            onClick={() => this.updateRouteState('/source-accounts')}
          >
            <MenuItemContainer>
              <IconSvg type="cluster" color={buttonNormal} hoverColor={buttonHover} size={20} />
              <FormattedMessage id="common.sourceAccounts" />
            </MenuItemContainer>
          </StyledMenuItem>
          <StyledMenuItem key="triple-cloud" onClick={() => this.updateRouteState('/triple-cloud')}>
            <MenuItemContainer>
              <IconSvg
                type="cloud-server"
                color={buttonNormal}
                hoverColor={buttonHover}
                size={20}
              />
              <FormattedMessage id="triplecloud.triplecloud" />
            </MenuItemContainer>
          </StyledMenuItem>
          <StyledMenuItem key="learning" onClick={() => this.updateRouteState('/learning')}>
            <MenuItemContainer>
              {userHasCourse ? (
                <div style={{ marginLeft: '50px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <IconSvg
                    type="education-center"
                    color={buttonNormal}
                    hoverColor={buttonHover}
                    size={20}
                  />
                  {userHasCourse && (
                    <Tag
                      color="green"
                      css={css`
                        line-height: 2;
                        cursor: auto;
                        margin-left: 5px;
                        font-size:9.5px;
                        height:19px;
                      `}
                    >
                      NEW
                    </Tag>
                  )}
                </div>
              ):(
                <IconSvg
                  type="education-center"
                  color={buttonNormal}
                  hoverColor={buttonHover}
                  size={20}
                />
              )}
              <SiderTitleWrapper>
                <FormattedMessage id={userHasCourse ? "common.onlineCourse" :"common.educationCenter"} />
              </SiderTitleWrapper>
            </MenuItemContainer>
          </StyledMenuItem>
        </StyledMenu>
      </AntSider>
    );
  }
}
export default withBreakpoints(Sider);
